import { useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend } from "recharts";

// ─── Maghil Design Tokens ────────────────────────────────────────────────────
const T = {
  bg: "#F5F6FA", surface: "#FFFFFF", card: "#FFFFFF", hover: "#F0F1F5",
  accent: "#D23A3B", accentSoft: "rgba(210,58,59,0.08)", accentGlow: "rgba(210,58,59,0.15)",
  green: "#16A34A", greenSoft: "rgba(22,163,74,0.08)",
  red: "#DC2626", redSoft: "rgba(220,38,38,0.08)",
  orange: "#D97706", orangeSoft: "rgba(217,119,6,0.08)",
  t1: "#1A1D2E", t2: "#5A6178", t3: "#9CA3B8",
  cardBorder: "#E2E5EC", divider: "#E8EAF0",
  shadow: "0 1px 4px rgba(0,0,0,0.05)",
  sidebarShadow: "2px 0 8px rgba(0,0,0,0.03)",
  topbarShadow: "0 1px 3px rgba(0,0,0,0.04)",
  font: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

// ─── Mock Data ───────────────────────────────────────────────────────────────
const topItemsRevenue = [
  { name: "Classic Cheeseburger", revenue: 18420, qty: 1842 },
  { name: "Margherita Pizza", revenue: 15890, qty: 1324 },
  { name: "Grilled Chicken Bowl", revenue: 13250, qty: 1105 },
  { name: "Fish & Chips", revenue: 11780, qty: 982 },
  { name: "Caesar Salad", revenue: 9340, qty: 1168 },
  { name: "BBQ Ribs Platter", revenue: 8920, qty: 594 },
  { name: "Shrimp Tacos", revenue: 8150, qty: 815 },
  { name: "Mushroom Risotto", revenue: 7630, qty: 636 },
  { name: "Buffalo Wings", revenue: 7210, qty: 1202 },
  { name: "Truffle Fries", revenue: 6890, qty: 1378 },
];

const bottomItemsRevenue = [
  { name: "House Salad", revenue: 890, qty: 178 },
  { name: "Breadstick Basket", revenue: 1120, qty: 448 },
  { name: "Soup of the Day", revenue: 1340, qty: 268 },
  { name: "Kids Mac & Cheese", revenue: 1580, qty: 395 },
  { name: "Seasonal Sorbet", revenue: 1690, qty: 338 },
];

const categoryData = [
  { name: "Entrées", value: 48200, pct: 38.2 },
  { name: "Appetizers", value: 22100, pct: 17.5 },
  { name: "Sides", value: 18900, pct: 15.0 },
  { name: "Beverages", value: 16300, pct: 12.9 },
  { name: "Desserts", value: 11800, pct: 9.4 },
  { name: "Kids Menu", value: 8900, pct: 7.0 },
];

const categoryColors = ["#D23A3B", "#E86B6B", "#F09494", "#D97706", "#16A34A", "#9CA3B8"];

const pmixData = [
  { name: "Classic Cheeseburger", pct: 14.2 },
  { name: "Buffalo Wings", pct: 9.3 },
  { name: "Truffle Fries", pct: 10.6 },
  { name: "Caesar Salad", pct: 9.0 },
  { name: "Margherita Pizza", pct: 10.2 },
  { name: "Grilled Chicken Bowl", pct: 8.5 },
  { name: "Fish & Chips", pct: 7.6 },
  { name: "Shrimp Tacos", pct: 6.3 },
  { name: "Mushroom Risotto", pct: 4.9 },
  { name: "BBQ Ribs Platter", pct: 4.6 },
  { name: "House Salad", pct: 1.4 },
  { name: "Other", pct: 13.4 },
].sort((a, b) => b.pct - a.pct);

const channelItems = [
  { name: "Classic Cheeseburger", dinein: 7800, delivery: 4200, doordash: 3800, ubereats: 2620 },
  { name: "Margherita Pizza", dinein: 5200, delivery: 3900, doordash: 4100, ubereats: 2690 },
  { name: "Grilled Chicken Bowl", dinein: 3100, delivery: 5400, doordash: 2850, ubereats: 1900 },
  { name: "Fish & Chips", dinein: 6200, delivery: 2100, doordash: 2080, ubereats: 1400 },
  { name: "Caesar Salad", dinein: 4800, delivery: 1900, doordash: 1540, ubereats: 1100 },
  { name: "BBQ Ribs Platter", dinein: 5100, delivery: 1500, doordash: 1420, ubereats: 900 },
];

const channelColors = { dinein: "#D23A3B", delivery: "#E86B6B", doordash: "#D97706", ubereats: "#16A34A" };

const addonData = [
  { name: "Classic Cheeseburger", addon: 3240, rate: 68 },
  { name: "Buffalo Wings", addon: 2180, rate: 72 },
  { name: "Grilled Chicken Bowl", addon: 1960, rate: 55 },
  { name: "Margherita Pizza", addon: 1740, rate: 48 },
  { name: "Fish & Chips", addon: 1520, rate: 42 },
  { name: "Shrimp Tacos", addon: 1180, rate: 38 },
  { name: "BBQ Ribs Platter", addon: 890, rate: 35 },
];

const refundData = [
  { name: "Caesar Salad", rate: 8.2, qty: 96, reason: "Wrong order" },
  { name: "Fish & Chips", rate: 6.1, qty: 60, reason: "Quality complaint" },
  { name: "Soup of the Day", rate: 5.8, qty: 16, reason: "Cold food" },
  { name: "Mushroom Risotto", rate: 4.3, qty: 27, reason: "Long wait" },
  { name: "Shrimp Tacos", rate: 3.9, qty: 32, reason: "Missing item" },
];

// All item names for search/filter across the app
const allItemNames = [...topItemsRevenue, ...bottomItemsRevenue].map(i => i.name);
const allCategories = ["Entrées", "Appetizers", "Sides", "Beverages", "Desserts", "Kids Menu"];

// Generate trend data for every item so any item can be charted
const trendLineColors = [T.accent, T.orange, T.green, "#7C5CFC", "#0EA5E9"];
const trendData = (() => {
  const baselines = {};
  allItemNames.forEach(name => { baselines[name] = 100 + Math.floor(Math.random() * 500); });
  const days = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(2026, 2, 6);
    d.setDate(d.getDate() - i);
    const row = { date: `${d.getMonth() + 1}/${d.getDate()}` };
    allItemNames.forEach(name => { row[name] = baselines[name] + Math.floor(Math.random() * 150); });
    days.push(row);
  }
  return days;
})();

const fullTableData = topItemsRevenue.map((item, i) => ({
  ...item,
  category: ["Entrées", "Entrées", "Entrées", "Entrées", "Appetizers", "Entrées", "Entrées", "Entrées", "Appetizers", "Sides"][i],
  pmix: pmixData.find(p => p.name === item.name)?.pct || 0,
  addonRev: addonData.find(a => a.name === item.name)?.addon || 0,
  refundQty: refundData.find(r => r.name === item.name)?.qty || 0,
}));

// ─── Formatters ──────────────────────────────────────────────────────────────
const fmt = (n: any) => n >= 1000 ? `$${(n / 1000).toFixed(1)}K` : `$${n}`;
const fmtFull = (n: any) => `$${n.toLocaleString()}`;
const fmtNum = (n: any) => n.toLocaleString();

// ─── Nav Items ───────────────────────────────────────────────────────────────
const navGroups = [
  { emoji: "💰", label: "Sales", items: ["Sales Summary", "Hourly Sales", "Order Channels"] },
  { emoji: "🍽️", label: "Menu", items: ["Item Sales", "Category Sales", "Discounts & Offers", "Product & Menu Performance"], active: "Product & Menu Performance" },
  { emoji: "👥", label: "Team", items: ["Staff Performance", "Voids & Exceptions", "Refunds"] },
  { emoji: "💳", label: "Payments", items: ["Tender Types", "EOD Reconciliation"] },
  { emoji: "🙋", label: "Guests", items: ["Check-In Analytics", "Party & Wait Times"] },
  { emoji: "📍", label: "Locations", items: ["Location Benchmark"] },
];

// ─── Shared Styles ───────────────────────────────────────────────────────────
const cardStyle = {
  background: T.card, borderRadius: 14, border: `1px solid ${T.cardBorder}`,
  boxShadow: T.shadow, padding: 22, marginBottom: 0,
};
const cardTitle = { fontSize: 14, fontWeight: 600, color: T.t1, margin: 0, marginBottom: 4 };
const cardSubtitle = { fontSize: 11, fontWeight: 400, color: T.t3, margin: 0, marginBottom: 16 };

// ─── Components ──────────────────────────────────────────────────────────────

function KPICard({ label, value, delta, prefix = "$" }: any) {
  const isPositive = delta >= 0;
  return (
    <div style={{ ...cardStyle, flex: 1, minWidth: 180 }}>
      <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, marginBottom: 6 }}>
        {prefix === "$" ? fmtFull(value) : fmtNum(value)}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: isPositive ? T.green : T.red, display: "flex", alignItems: "center", gap: 4 }}>
        <span>{isPositive ? "↑" : "↓"}</span>
        <span>{Math.abs(delta).toFixed(1)}%</span>
        <span style={{ color: T.t3, fontWeight: 400, marginLeft: 4 }}>vs prior period</span>
      </div>
    </div>
  );
}

function FilterPill({ label, active, onClick }: any) {
  return (
    <button onClick={onClick} style={{
      padding: "4px 12px", borderRadius: 6, border: "none", cursor: "pointer",
      fontSize: 11.5, fontWeight: 500, fontFamily: T.font, transition: "all 0.15s",
      background: active ? T.accentSoft : "transparent",
      color: active ? T.accent : T.t2,
    }}>{label}</button>
  );
}

function HorizontalBarChart({ data, valueKey, labelKey = "name", color = T.accent, maxVal, formatValue = fmtFull }: any) {
  const max = maxVal || Math.max(...data.map(d => d[valueKey]));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {data.map((item, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 160, fontSize: 12, fontWeight: 500, color: T.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>{item[labelKey]}</div>
          <div style={{ flex: 1, background: T.hover, borderRadius: 3, height: 6, overflow: "hidden" }}>
            <div style={{ width: `${(item[valueKey] / max) * 100}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.6s ease" }} />
          </div>
          <div style={{ width: 70, fontSize: 12, fontWeight: 600, color: T.t1, textAlign: "right", flexShrink: 0 }}>{formatValue(item[valueKey])}</div>
        </div>
      ))}
    </div>
  );
}

function DataTable({ columns, data }: any) {
  const [sortCol, setSortCol] = useState<any>(null);
  const [sortDir, setSortDir] = useState("desc");
  const sorted = useMemo(() => {
    if (!sortCol) return data;
    return [...data].sort((a, b) => sortDir === "desc" ? (b[sortCol] - a[sortCol]) : (a[sortCol] - b[sortCol]));
  }, [data, sortCol, sortDir]);

  const handleSort = (key: any) => {
    if (sortCol === key) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortCol(key); setSortDir("desc"); }
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font }}>
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key} onClick={() => col.sortable && handleSort(col.key)} style={{
                fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase",
                textAlign: col.align || "left", padding: "10px 12px", borderBottom: `1px solid ${T.divider}`,
                cursor: col.sortable ? "pointer" : "default", userSelect: "none", whiteSpace: "nowrap",
              }}>
                {col.label} {sortCol === col.key ? (sortDir === "desc" ? "↓" : "↑") : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${T.divider}` }}
              onMouseEnter={e => e.currentTarget.style.background = T.hover}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              {columns.map(col => (
                <td key={col.key} style={{
                  fontSize: 12, fontWeight: col.bold ? 500 : 400, color: col.color || T.t1,
                  textAlign: col.align || "left", padding: "10px 12px", whiteSpace: "nowrap",
                }}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Searchable Multi-Select Dropdown ────────────────────────────────────────
function SearchableMultiSelect({ label, options, selected, onChange, placeholder = "Search..." }: any) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  const toggle = (val: any) => {
    onChange(selected.includes(val) ? selected.filter(s => s !== val) : [...selected, val]);
  };
  return (
    <div style={{ position: "relative" }}>
      <div onClick={() => setOpen(!open)} style={{
        display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 6,
        border: `1px solid ${selected.length > 0 ? T.accent : T.cardBorder}`,
        background: selected.length > 0 ? T.accentSoft : T.surface,
        cursor: "pointer", fontSize: 11.5, fontWeight: 500, color: selected.length > 0 ? T.accent : T.t2,
        minWidth: 120, userSelect: "none",
      }}>
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected.length === 0 ? label : selected.length === 1 ? selected[0] : `${selected.length} selected`}
        </span>
        <span style={{ fontSize: 8, color: T.t3 }}>▼</span>
      </div>
      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 49 }} onClick={() => { setOpen(false); setSearch(""); }} />
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 50, minWidth: 220,
            background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.10)", overflow: "hidden",
          }}>
            <div style={{ padding: "8px 10px", borderBottom: `1px solid ${T.divider}` }}>
              <input
                autoFocus
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder={placeholder}
                style={{
                  width: "100%", border: "none", outline: "none", fontSize: 12, fontFamily: T.font,
                  color: T.t1, background: "transparent", padding: 0,
                }}
              />
            </div>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              {filtered.length === 0 && (
                <div style={{ padding: "10px 12px", fontSize: 11, color: T.t3 }}>No results</div>
              )}
              {filtered.map(opt => {
                const isSelected = selected.includes(opt);
                return (
                  <div key={opt} onClick={() => toggle(opt)} style={{
                    padding: "7px 12px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
                    background: isSelected ? T.accentSoft : "transparent", color: isSelected ? T.accent : T.t1,
                    fontWeight: isSelected ? 600 : 400,
                  }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = T.hover; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                  >
                    <span style={{
                      width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${isSelected ? T.accent : T.cardBorder}`,
                      background: isSelected ? T.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 9, color: "#fff", flexShrink: 0,
                    }}>{isSelected ? "✓" : ""}</span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opt}</span>
                  </div>
                );
              })}
            </div>
            {selected.length > 0 && (
              <div style={{ padding: "6px 10px", borderTop: `1px solid ${T.divider}`, display: "flex", justifyContent: "flex-end" }}>
                <button onClick={() => { onChange([]); setSearch(""); }} style={{
                  border: "none", background: "none", fontSize: 11, color: T.accent, cursor: "pointer", fontFamily: T.font, fontWeight: 500, padding: "2px 4px",
                }}>Clear all</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Export Utilities ────────────────────────────────────────────────────────
function exportCSV(filename: any, headers: any, rows: any) {
  const escape = (v: any) => {
    const s = String(v ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.map(escape).join(","), ...rows.map(r => r.map(escape).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${filename}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function exportPDF(title: any, headers: any, rows: any) {
  const w = window.open("", "_blank");
  if (!w) return;
  const thStyle = `style="border:1px solid #E2E5EC;padding:8px 12px;text-align:left;font-size:11px;font-weight:600;color:#5A6178;background:#F5F6FA;text-transform:uppercase"`;
  const tdStyle = `style="border:1px solid #E2E5EC;padding:8px 12px;font-size:12px;color:#1A1D2E"`;
  const ths = headers.map(h => `<th ${thStyle}>${h}</th>`).join("");
  const trs = rows.map(r => `<tr>${r.map(c => `<td ${tdStyle}>${c}</td>`).join("")}</tr>`).join("");
  w.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
    <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>
      body{font-family:'DM Sans',sans-serif;padding:40px;color:#1A1D2E}
      h1{font-size:18px;font-weight:700;margin-bottom:4px}
      .sub{font-size:12px;color:#5A6178;margin-bottom:20px}
      table{width:100%;border-collapse:collapse;margin-top:8px}
      .brand{display:flex;align-items:center;gap:10px;margin-bottom:24px}
      .logo{width:28px;height:28px;border-radius:8px;background:linear-gradient(135deg,#D23A3B,#E86B6B);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px}
      .bname{font-size:14px;font-weight:700} .bsub{font-size:9px;color:#9CA3B8;letter-spacing:0.5px}
      @media print{body{padding:20px}button{display:none!important}}
    </style></head><body>
    <div class="brand"><div class="logo">M</div><div><div class="bname">Maghil</div><div class="bsub">ANALYTICS</div></div></div>
    <h1>${title}</h1>
    <div class="sub">Exported on ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
    <table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>
    <br/><button onclick="window.print()" style="padding:8px 20px;background:#D23A3B;color:#fff;border:none;border-radius:6px;font-family:'DM Sans';font-size:12px;font-weight:600;cursor:pointer">Print / Save as PDF</button>
  </body></html>`);
  w.document.close();
}

// ─── Export Dropdown Menu ────────────────────────────────────────────────────
function ExportMenu({ onCSV, onPDF }: any) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen(!open)} style={{
        display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 6,
        border: `1px solid ${T.cardBorder}`, background: T.surface, cursor: "pointer",
        fontSize: 11, fontWeight: 500, color: T.t2, fontFamily: T.font,
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.t3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
        Export
      </button>
      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 49 }} onClick={() => setOpen(false)} />
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 50, minWidth: 140,
            background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.10)", overflow: "hidden",
          }}>
            <div onClick={() => { onCSV(); setOpen(false); }} style={{
              padding: "9px 14px", fontSize: 12, color: T.t1, cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
            }} onMouseEnter={e => e.currentTarget.style.background = T.hover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
              Export as CSV
            </div>
            <div style={{ height: 1, background: T.divider }} />
            <div onClick={() => { onPDF(); setOpen(false); }} style={{
              padding: "9px 14px", fontSize: 12, color: T.t1, cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
            }} onMouseEnter={e => e.currentTarget.style.background = T.hover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/>
              </svg>
              Export as PDF
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Custom donut tooltip
const DonutTooltip = ({ active, payload }: any) => {
  if (active && payload?.length) {
    return (
      <div style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: "8px 12px", boxShadow: T.shadow, fontFamily: T.font }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{payload[0].name}</div>
        <div style={{ fontSize: 11, color: T.t2, marginTop: 2 }}>{fmtFull(payload[0].value)} ({payload[0].payload.pct}%)</div>
      </div>
    );
  }
  return null;
};

const BarTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: "8px 12px", boxShadow: T.shadow, fontFamily: T.font }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, marginBottom: 4 }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ fontSize: 11, color: T.t2, display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, display: "inline-block" }} />
            {p.name}: {fmtFull(p.value)}
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// ─── Item Detail Panel ───────────────────────────────────────────────────────
function ItemDetailPanel({ item, onClose }: any) {
  if (!item) return null;
  const addon = addonData.find(a => a.name === item.name);
  const refund = refundData.find(r => r.name === item.name);
  const channel = channelItems.find(c => c.name === item.name);
  const channelTotal = channel ? channel.dinein + channel.delivery + channel.doordash + channel.ubereats : 0;

  return (
    <div style={{ position: "fixed", top: 0, right: 0, width: 420, height: "100vh", background: T.surface, boxShadow: "-4px 0 20px rgba(0,0,0,0.08)", zIndex: 100, overflowY: "auto", fontFamily: T.font }}>
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 11, color: T.t3, marginBottom: 4 }}>Item Detail</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.t1 }}>{item.name}</div>
        </div>
        <button onClick={onClose} style={{ background: T.hover, border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 16, color: T.t2, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
      </div>
      <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
        {/* Mini KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          {[
            { label: "Revenue", value: fmtFull(item.revenue) },
            { label: "Qty Sold", value: fmtNum(item.qty) },
            { label: "PMIX", value: `${(pmixData.find(p => p.name === item.name)?.pct || 0).toFixed(1)}%` },
          ].map((k, i) => (
            <div key={i} style={{ background: T.bg, borderRadius: 10, padding: "12px 10px", textAlign: "center" }}>
              <div style={{ fontSize: 8, fontWeight: 400, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 6 }}>{k.label}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.t1 }}>{k.value}</div>
            </div>
          ))}
        </div>
        {/* Addon */}
        {addon && (
          <div style={{ ...cardStyle, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 10 }}>Add-On Performance</div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.t2, marginBottom: 6 }}>
              <span>Add-On Revenue</span><span style={{ fontWeight: 600, color: T.t1 }}>{fmtFull(addon.addon)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.t2 }}>
              <span>Attach Rate</span><span style={{ fontWeight: 600, color: T.accent }}>{addon.rate}%</span>
            </div>
          </div>
        )}
        {/* Channel Breakdown */}
        {channel && (
          <div style={{ ...cardStyle, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 12 }}>Channel Split</div>
            {[
              { label: "Dine-In", val: channel.dinein, color: channelColors.dinein },
              { label: "Delivery", val: channel.delivery, color: channelColors.delivery },
              { label: "DoorDash", val: channel.doordash, color: channelColors.doordash },
              { label: "Uber Eats", val: channel.ubereats, color: channelColors.ubereats },
            ].map((ch, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
                  <span style={{ color: T.t2 }}>{ch.label}</span>
                  <span style={{ fontWeight: 600, color: T.t1 }}>{fmtFull(ch.val)} ({((ch.val / channelTotal) * 100).toFixed(0)}%)</span>
                </div>
                <div style={{ background: T.hover, borderRadius: 3, height: 5 }}>
                  <div style={{ width: `${(ch.val / channelTotal) * 100}%`, height: "100%", background: ch.color, borderRadius: 3, opacity: 0.7 }} />
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Refund */}
        {refund ? (
          <div style={{ ...cardStyle, padding: 16, border: `1px solid ${T.redSoft}` }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.red, marginBottom: 10 }}>Refund & Void Activity</div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.t2, marginBottom: 4 }}>
              <span>Refund Rate</span><span style={{ fontWeight: 600, color: T.red }}>{refund.rate}%</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.t2, marginBottom: 4 }}>
              <span>Refunded Qty</span><span style={{ fontWeight: 600, color: T.t1 }}>{refund.qty}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.t2 }}>
              <span>Top Reason</span><span style={{ fontWeight: 500, color: T.orange }}>{refund.reason}</span>
            </div>
          </div>
        ) : (
          <div style={{ ...cardStyle, padding: 16, background: T.greenSoft, border: `1px solid rgba(22,163,74,0.15)` }}>
            <div style={{ fontSize: 12, color: T.green, fontWeight: 500 }}>No refunds or voids recorded for this item.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function ProductMenuPerformanceReport() {
  const [dateRange, setDateRange] = useState("7d");
  const [source, setSource] = useState("all");
  const [sellerView, setSellerView] = useState("top");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [expandedNav, setExpandedNav] = useState("Menu");
  // Trend chart selected items (default 3)
  const [trendItems, setTrendItems] = useState(["Classic Cheeseburger", "Margherita Pizza", "Grilled Chicken Bowl"]);
  // Full table filters
  const [tableCategoryFilter, setTableCategoryFilter] = useState([]);
  const [tableItemFilter, setTableItemFilter] = useState([]);

  const filteredTableData = useMemo(() => {
    let data = fullTableData;
    if (tableCategoryFilter.length > 0) data = data.filter(r => tableCategoryFilter.includes(r.category));
    if (tableItemFilter.length > 0) data = data.filter(r => tableItemFilter.includes(r.name));
    return data;
  }, [tableCategoryFilter, tableItemFilter]);

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: T.font, background: T.bg, overflow: "hidden" }}>
      {/* ─── SIDEBAR ─── */}
      <aside style={{ width: 250, background: T.surface, borderRight: `1px solid ${T.divider}`, boxShadow: T.sidebarShadow, display: "flex", flexDirection: "column", flexShrink: 0, overflowY: "auto" }}>
        {/* Logo */}
        <div style={{ padding: 20, borderBottom: `1px solid ${T.divider}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, #D23A3B, #E86B6B)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 16 }}>M</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.t1 }}>Maghil</div>
              <div style={{ fontSize: 10, fontWeight: 400, color: T.t3, letterSpacing: 0.5 }}>ANALYTICS</div>
            </div>
          </div>
        </div>
        {/* Outlet */}
        <div style={{ padding: "12px 16px" }}>
          <div style={{ background: T.hover, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: "8px 10px", fontSize: 11, fontWeight: 600, color: T.t1, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
            <span>Downtown Bistro</span>
            <span style={{ color: T.t3, fontSize: 10 }}>▼</span>
          </div>
        </div>
        {/* Nav Groups */}
        <nav style={{ flex: 1, padding: "4px 0" }}>
          {navGroups.map(group => (
            <div key={group.label}>
              <div onClick={() => setExpandedNav(expandedNav === group.label ? "" : group.label)}
                style={{ padding: "8px 16px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: expandedNav === group.label ? T.accent : T.t2, userSelect: "none" }}>
                <span>{group.emoji}</span>
                <span style={{ flex: 1 }}>{group.label}</span>
                <span style={{ fontSize: 10, color: T.t3, transform: expandedNav === group.label ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>▶</span>
              </div>
              {expandedNav === group.label && (
                <div style={{ paddingBottom: 4 }}>
                  {group.items.map(item => {
                    const isActive = group.active === item;
                    return (
                      <div key={item} style={{
                        padding: "8px 10px 8px 22px", marginLeft: 16, marginRight: 8, borderRadius: 6,
                        fontSize: 12, fontWeight: isActive ? 600 : 400, cursor: "pointer",
                        color: isActive ? T.accent : T.t2,
                        background: isActive ? T.accentSoft : "transparent",
                        borderLeft: isActive ? `2px solid ${T.accent}` : "2px solid transparent",
                      }}>{item}</div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </nav>
        {/* User */}
        <div style={{ padding: "16px 20px", borderTop: `1px solid ${T.divider}`, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 28, height: 28, borderRadius: "50%", background: T.accentSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: T.accent }}>JD</div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>John Doe</div>
            <div style={{ fontSize: 10, color: T.t3 }}>Restaurant Manager</div>
          </div>
        </div>
      </aside>

      {/* ─── MAIN AREA ─── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* ─── TOP BAR ─── */}
        <header style={{ height: 56, background: T.surface, borderBottom: `1px solid ${T.divider}`, boxShadow: T.topbarShadow, display: "flex", alignItems: "center", padding: "0 24px", gap: 16, flexShrink: 0, zIndex: 10 }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: T.t3 }}>Reports</span>
            <span style={{ fontSize: 12, color: T.t3 }}>›</span>
            <span style={{ fontSize: 12, color: T.t3 }}>Menu</span>
            <span style={{ fontSize: 12, color: T.t3 }}>›</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.t1 }}>Product & Menu Performance</span>
          </div>
          {/* Date Filters */}
          <div style={{ display: "flex", gap: 2 }}>
            {[["1d", "Today"], ["7d", "7 Days"], ["30d", "30 Days"], ["mtd", "MTD"]].map(([key, label]) => (
              <FilterPill key={key} label={label} active={dateRange === key} onClick={() => setDateRange(key)} />
            ))}
          </div>
          {/* Source Toggle */}
          <div style={{ display: "flex", border: `1px solid ${T.cardBorder}`, borderRadius: 6, overflow: "hidden" }}>
            {[["all", "All"], ["maghil", "Maghil"], ["3p", "3rd Party"]].map(([key, label]) => (
              <button key={key} onClick={() => setSource(key)} style={{
                padding: "4px 10px", border: "none", cursor: "pointer", fontSize: 11, fontWeight: 500, fontFamily: T.font,
                background: source === key ? T.accent : T.surface,
                color: source === key ? "#fff" : T.t2,
              }}>{label}</button>
            ))}
          </div>
          {/* Export */}
          <button style={{ padding: "6px 14px", background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, fontSize: 11.5, color: T.t2, cursor: "pointer", fontFamily: T.font }}>
            Export ↓
          </button>
        </header>

        {/* ─── CONTENT AREA ─── */}
        <main style={{ flex: 1, overflowY: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>

          {/* KPI Summary Row */}
          <div style={{ display: "flex", gap: 16 }}>
            <KPICard label="Total Item Revenue" value={126200} delta={8.3} />
            <KPICard label="Total Items Sold" value={12963} delta={5.1} prefix="" />
            <KPICard label="Avg Revenue / Item" value={9.74} delta={3.0} />
          </div>

          {/* Row 2: Top Items + Category Donut */}
          <div style={{ display: "flex", gap: 16 }}>
            {/* Top/Bottom Items */}
            <div style={{ ...cardStyle, flex: 1.2 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                  <p style={cardTitle}>{sellerView === "top" ? "Top 10 Items by Revenue" : "Bottom 5 Items by Revenue"}</p>

                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <FilterPill label="Top 10" active={sellerView === "top"} onClick={() => setSellerView("top")} />
                  <FilterPill label="Bottom 5" active={sellerView === "bottom"} onClick={() => setSellerView("bottom")} />
                  <ExportMenu
                    onCSV={() => {
                      const d = sellerView === "top" ? topItemsRevenue : bottomItemsRevenue;
                      exportCSV(sellerView === "top" ? "Top_10_Items_Revenue" : "Bottom_5_Items_Revenue",
                        ["Item", "Revenue", "Qty Sold"],
                        d.map(r => [r.name, r.revenue, r.qty])
                      );
                    }}
                    onPDF={() => {
                      const d = sellerView === "top" ? topItemsRevenue : bottomItemsRevenue;
                      exportPDF(sellerView === "top" ? "Top 10 Items by Revenue" : "Bottom 5 Items by Revenue",
                        ["Item", "Revenue", "Qty Sold"],
                        d.map(r => [r.name, `$${r.revenue.toLocaleString()}`, r.qty.toLocaleString()])
                      );
                    }}
                  />
                </div>
              </div>
              <HorizontalBarChart
                data={sellerView === "top" ? topItemsRevenue : bottomItemsRevenue}
                valueKey="revenue"
                color={sellerView === "top" ? T.accent : T.red}
              />
            </div>
            {/* Category Donut */}
            <div style={{ ...cardStyle, flex: 0.8, display: "flex", flexDirection: "column" }}>
              <p style={cardTitle}>Category Revenue Breakdown</p>

              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={categoryData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2} strokeWidth={0}>
                      {categoryData.map((_, i) => <Cell key={i} fill={categoryColors[i % categoryColors.length]} />)}
                    </Pie>
                    <Tooltip content={<DonutTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 8 }}>
                {categoryData.map((cat, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: categoryColors[i], display: "inline-block" }} />
                    <span style={{ color: T.t2 }}>{cat.name}</span>
                    <span style={{ fontWeight: 600, color: T.t1 }}>{cat.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Row 3: PMIX */}
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <p style={{ ...cardTitle, marginBottom: 0 }}>Product Mix (PMIX) — Top 15 Items</p>
              <ExportMenu
                onCSV={() => {
                  exportCSV("Product_Mix_PMIX",
                    ["Item", "PMIX %"],
                    pmixData.map(r => [r.name, `${r.pct.toFixed(1)}%`])
                  );
                }}
                onPDF={() => {
                  exportPDF("Product Mix (PMIX) — Top 15 Items",
                    ["Item", "PMIX %"],
                    pmixData.map(r => [r.name, `${r.pct.toFixed(1)}%`])
                  );
                }}
              />
            </div>
            <HorizontalBarChart data={pmixData} valueKey="pct" color={T.accent} formatValue={v => `${v.toFixed(1)}%`} maxVal={20} />
          </div>

          {/* Row 4: Channel Revenue (Stacked Bar) */}
          <div style={cardStyle}>
            <p style={cardTitle}>Item Revenue by Channel</p>

            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={channelItems} layout="vertical" margin={{ left: 120, right: 20, top: 5, bottom: 5 }}>
                <XAxis type="number" tickFormatter={v => fmt(v)} tick={{ fontSize: 11, fill: T.t3 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: T.t1 }} axisLine={false} tickLine={false} width={120} />
                <Tooltip content={<BarTooltip />} />
                <Bar dataKey="dinein" name="Dine-In" stackId="a" fill={channelColors.dinein} radius={[0, 0, 0, 0]} />
                <Bar dataKey="delivery" name="Delivery" stackId="a" fill={channelColors.delivery} />
                <Bar dataKey="doordash" name="DoorDash" stackId="a" fill={channelColors.doordash} />
                <Bar dataKey="ubereats" name="Uber Eats" stackId="a" fill={channelColors.ubereats} radius={[0, 4, 4, 0]} />
                <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 11, color: T.t2, paddingTop: 8 }} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Row 5: Add-On + Refund/Void */}
          <div style={{ display: "flex", gap: 16 }}>
            {/* Add-On Revenue */}
            <div style={{ ...cardStyle, flex: 1 }}>
              <p style={cardTitle}>Add-On Revenue Ranking</p>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {addonData.map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 140, fontSize: 12, fontWeight: 500, color: T.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>{item.name}</div>
                    <div style={{ flex: 1, background: T.hover, borderRadius: 3, height: 6, overflow: "hidden" }}>
                      <div style={{ width: `${(item.addon / 3500) * 100}%`, height: "100%", background: T.accent, borderRadius: 3, opacity: 0.6 }} />
                    </div>
                    <div style={{ width: 60, fontSize: 11, fontWeight: 600, color: T.t1, textAlign: "right" }}>{fmtFull(item.addon)}</div>
                    <div style={{ width: 40, fontSize: 10, fontWeight: 500, color: T.accent, textAlign: "right", background: T.accentSoft, borderRadius: 4, padding: "2px 6px" }}>{item.rate}%</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Refund & Void */}
            <div style={{ ...cardStyle, flex: 1 }}>
              <p style={cardTitle}>Refund & Void Rate by Item</p>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {refundData.map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 140, fontSize: 12, fontWeight: 500, color: T.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>{item.name}</div>
                    <div style={{ flex: 1, background: T.hover, borderRadius: 3, height: 6, overflow: "hidden" }}>
                      <div style={{ width: `${(item.rate / 10) * 100}%`, height: "100%", background: T.red, borderRadius: 3, opacity: 0.65 }} />
                    </div>
                    <div style={{ width: 40, fontSize: 11, fontWeight: 600, color: T.red, textAlign: "right" }}>{item.rate}%</div>
                    <div style={{ fontSize: 10, color: T.orange, fontWeight: 500, background: T.orangeSoft, borderRadius: 4, padding: "2px 6px", whiteSpace: "nowrap" }}>{item.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Row 6: Item Trend (Line Chart) */}
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <p style={{ ...cardTitle, marginBottom: 0 }}>Item Revenue Trend — Last 30 Days</p>
              <SearchableMultiSelect
                label="Select Items"
                options={allItemNames}
                selected={trendItems}
                onChange={(val) => setTrendItems(val.slice(0, 5))}
                placeholder="Search items..."
              />
            </div>
            {trendItems.length === 0 ? (
              <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: T.t3, fontSize: 13 }}>
                Select items above to view their revenue trend
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={trendData} margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.divider} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: T.t3 }} axisLine={false} tickLine={false} interval={4} />
                  <YAxis tick={{ fontSize: 10, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                  <Tooltip contentStyle={{ fontFamily: T.font, fontSize: 11, borderRadius: 8, border: `1px solid ${T.cardBorder}` }} />
                  {trendItems.map((name, i) => (
                    <Line key={name} type="monotone" dataKey={name} name={name} stroke={trendLineColors[i % trendLineColors.length]} strokeWidth={2} dot={false} />
                  ))}
                  <Legend iconType="line" iconSize={12} wrapperStyle={{ fontSize: 11, color: T.t2, paddingTop: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Row 7: Full Item Performance Table */}
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>
                <p style={cardTitle}>Full Item Performance Table</p>
                <p style={{ ...cardSubtitle, marginBottom: 0 }}>Click any item row to view detail panel</p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ fontSize: 11, color: T.t3 }}>Showing {filteredTableData.length} of {fullTableData.length} items</div>
                <ExportMenu
                  onCSV={() => {
                    exportCSV("Item_Performance_Table",
                      ["Item", "Category", "Qty Sold", "Revenue", "PMIX %", "Add-On Rev", "Refunds"],
                      filteredTableData.map(r => [r.name, r.category, r.qty, r.revenue, `${r.pmix.toFixed(1)}%`, r.addonRev || "—", r.refundQty])
                    );
                  }}
                  onPDF={() => {
                    exportPDF("Full Item Performance Table",
                      ["Item", "Category", "Qty Sold", "Revenue", "PMIX %", "Add-On Rev", "Refunds"],
                      filteredTableData.map(r => [r.name, r.category, r.qty.toLocaleString(), `$${r.revenue.toLocaleString()}`, `${r.pmix.toFixed(1)}%`, r.addonRev ? `$${r.addonRev.toLocaleString()}` : "—", r.refundQty || "0"])
                    );
                  }}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <SearchableMultiSelect
                label="Category"
                options={allCategories}
                selected={tableCategoryFilter}
                onChange={setTableCategoryFilter}
                placeholder="Search categories..."
              />
              <SearchableMultiSelect
                label="Item"
                options={allItemNames}
                selected={tableItemFilter}
                onChange={setTableItemFilter}
                placeholder="Type to search items..."
              />
            </div>
            <DataTable
              columns={[
                { key: "name", label: "Item", bold: true, render: (v) => <span style={{ cursor: "pointer", color: T.accent, fontWeight: 600 }} onClick={() => setSelectedItem(fullTableData.find(i => i.name === v))}>{v}</span> },
                { key: "category", label: "Category", color: T.t2 },
                { key: "qty", label: "Qty Sold", align: "right", sortable: true, render: v => fmtNum(v) },
                { key: "revenue", label: "Revenue", align: "right", sortable: true, render: v => fmtFull(v) },
                { key: "pmix", label: "PMIX %", align: "right", sortable: true, render: v => `${v.toFixed(1)}%` },
                { key: "addonRev", label: "Add-On Rev", align: "right", sortable: true, render: v => v > 0 ? fmtFull(v) : "—" },
                { key: "refundQty", label: "Refunds", align: "right", sortable: true, render: v => v > 0 ? <span style={{ color: T.red, fontWeight: 600 }}>{v}</span> : <span style={{ color: T.green }}>0</span> },
              ]}
              data={filteredTableData}
            />
          </div>

          {/* Footer Spacer */}
          <div style={{ height: 40 }} />
        </main>
      </div>

      {/* Item Detail Slide-Over Panel */}
      {selectedItem && <ItemDetailPanel item={selectedItem} onClose={() => setSelectedItem(null)} />}
    </div>
  );
}
